import { useEffect, useState } from "react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { Tenant } from "../../../../types/tenant";

type AttendanceWindow = Pick<
  Tenant,
  | "schedulingStartTime"
  | "schedulingEndTime"
  | "schedulingAllowSaturday"
  | "schedulingSaturdayStartTime"
  | "schedulingSaturdayEndTime"
>;

export default function AttendanceWindowConfig() {
  const { user } = useAuth();
  const { error, success } = useToast();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user?.appName) return;

    const fetchTenant = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<Tenant>(`/tenant/${user.appName}`);
        setTenant({
          ...data,
          schedulingStartTime: data.schedulingStartTime ?? "07:00",
          schedulingEndTime: data.schedulingEndTime ?? "18:00",
          schedulingAllowSaturday: !!data.schedulingAllowSaturday,
          schedulingSaturdayStartTime:
            data.schedulingSaturdayStartTime ??
            data.schedulingStartTime ??
            "07:00",
          schedulingSaturdayEndTime:
            data.schedulingSaturdayEndTime ?? data.schedulingEndTime ?? "18:00",
        });
      } catch (err) {
        error(getApiErrorMessage(err));
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [user?.appName, error]);

  const updateField = <Field extends keyof AttendanceWindow>(
    field: Field,
    value: AttendanceWindow[Field],
  ) => {
    setTenant((current) =>
      current ? { ...current, [field]: value } : current,
    );
  };

  const toMinutes = (value: string, isEnd = false) => {
    const [hours, minutes] = value.split(":").map(Number);
    const total = hours * 60 + minutes;
    return isEnd && total === 0 ? 24 * 60 : total;
  };

  const isValidWindow = (start?: string, end?: string) =>
    !!start && !!end && toMinutes(end, true) > toMinutes(start);

  const handleSave = async () => {
    if (!tenant || isSaving) return;

    if (
      !isValidWindow(tenant.schedulingStartTime, tenant.schedulingEndTime) ||
      (tenant.schedulingAllowSaturday &&
        !isValidWindow(
          tenant.schedulingSaturdayStartTime,
          tenant.schedulingSaturdayEndTime,
        ))
    ) {
      error("Informe horários válidos, com o fim posterior ao início.");
      return;
    }

    try {
      setIsSaving(true);
      await api.patch("/tenant/attendance-window", {
        schedulingStartTime: tenant.schedulingStartTime,
        schedulingEndTime: tenant.schedulingEndTime,
        schedulingAllowSaturday: tenant.schedulingAllowSaturday,
        schedulingSaturdayStartTime: tenant.schedulingSaturdayStartTime,
        schedulingSaturdayEndTime: tenant.schedulingSaturdayEndTime,
      });
      success("Janela de atendimento atualizada com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="app-page-title">Janela de atendimento</h1>
          <p className="text-sm text-slate-500">
            Defina os horários em que a equipe realiza atendimento humano.
          </p>
        </div>
        {tenant && (
          <LoadingButton
            type="button"
            onClick={handleSave}
            isLoading={isSaving}
            loadingLabel="Salvando"
            className="btn btn-neutral"
          >
            Salvar alterações
          </LoadingButton>
        )}
      </div>

      {isLoading && (
        <p className="text-sm text-slate-600">Carregando configurações...</p>
      )}

      {!isLoading && !tenant && (
        <div className="app-empty-state">
          Não foi possível carregar as configurações do tenant.
        </div>
      )}

      {!isLoading && tenant && (
        <div className="app-card max-w-3xl space-y-5 p-4 sm:p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="form-label" htmlFor="attendance-start">
                Início do atendimento
              </label>
              <input
                id="attendance-start"
                type="time"
                className="form-input"
                value={tenant.schedulingStartTime}
                onChange={(event) =>
                  updateField("schedulingStartTime", event.target.value)
                }
              />
            </div>
            <div>
              <label className="form-label" htmlFor="attendance-end">
                Fim do atendimento
              </label>
              <input
                id="attendance-end"
                type="time"
                className="form-input"
                value={tenant.schedulingEndTime}
                onChange={(event) =>
                  updateField("schedulingEndTime", event.target.value)
                }
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={!!tenant.schedulingAllowSaturday}
              onChange={(event) =>
                updateField("schedulingAllowSaturday", event.target.checked)
              }
            />
            Atender aos sábados
          </label>

          {tenant.schedulingAllowSaturday && (
            <div className="grid gap-4 border-t border-slate-200 pt-5 md:grid-cols-2">
              <div>
                <label className="form-label" htmlFor="saturday-start">
                  Início do atendimento no sábado
                </label>
                <input
                  id="saturday-start"
                  type="time"
                  className="form-input"
                  value={tenant.schedulingSaturdayStartTime}
                  onChange={(event) =>
                    updateField(
                      "schedulingSaturdayStartTime",
                      event.target.value,
                    )
                  }
                />
              </div>
              <div>
                <label className="form-label" htmlFor="saturday-end">
                  Fim do atendimento no sábado
                </label>
                <input
                  id="saturday-end"
                  type="time"
                  className="form-input"
                  value={tenant.schedulingSaturdayEndTime}
                  onChange={(event) =>
                    updateField("schedulingSaturdayEndTime", event.target.value)
                  }
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
